import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Avatar, Box, Menu, SvgIconProps, Typography } from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import "./translations";

import { AdminSettingsIcon, PersonIcon, ProfileSettingsIcon, SecurityIcon } from "@/assets";
import { getInitials } from "@/helpers/formatStringHelpers";
import { authorizationService } from "@/hooks/permissionsHooks/authorizationService";
import { useModuleContext } from "@/hooks/useModuleContext";
import { useAppSelector } from "@/store/hooks";

import { HeaderMenuItem } from "../HeaderMenuItem";
import { HeaderMenuSectionTitle } from "../HeaderMenuSectionTitle";

const InstitutionSettingsMenuIcon = (props: SvgIconProps): JSX.Element => <SettingsOutlinedIcon {...props} />;

export type UserMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  isInstitutionAdmin?: boolean;
};

export const UserMenu = ({ anchorEl, open, onClose, isInstitutionAdmin }: UserMenuProps): JSX.Element => {
  const { t } = useTranslation("UserMenu");
  const { authState } = useOktaAuth();
  const { isValuationPackage, isSettings } = useModuleContext();

  const user = useAppSelector((state) => state.applicationMain.userInfo);

  const isSuperAdmin = authorizationService.isAdmin(authState?.accessToken?.accessToken);

  const navigate = useNavigate();
  const navigateAndClose = (path: string): void => {
    navigate(path);
    onClose();
  };

  const onAdminAreaClick = (): void => {
    navigateAndClose("/backoffice");
  };

  const nameToColor = (name: string | undefined): string => {
    let hash = 0;
    if (!name) {
      return "#EEEEEE";
    }
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const color = [0, 0, 0]
      .map((_, i) => {
        const value = (hash >> (i * 8)) & 0xff;
        return value.toString(16).padStart(2, "0");
      })
      .join("");
    return `#${color}`;
  };

  const areSettingsAvailable = isSettings || isValuationPackage;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      {authState && authState.isAuthenticated && user && (
        <Box sx={{ p: "30px" }}>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <Avatar sx={{ width: 45, height: 45, bgcolor: nameToColor(user.user.name) }}>
              {getInitials(user.user.name)}
            </Avatar>
            <Box ml={2} sx={{ display: "flex", flexDirection: "column", rowGap: "8px" }}>
              <Typography variant="headerMenuTitle">{user.user.name}</Typography>
              <Typography variant="subText">{user.user.email}</Typography>
            </Box>
          </Box>

          {user.company.name && (
            <>
              <HeaderMenuSectionTitle title={t("companySettings.title")} />
              {user.company.name && <HeaderMenuItem label={user.company.name} />}
              {user.company.address && <HeaderMenuItem label={user.company.address} />}
            </>
          )}

          <HeaderMenuSectionTitle title={t("myProfile.title")} />
          <HeaderMenuItem
            onClick={areSettingsAvailable ? (): void => navigateAndClose("/settings/profile") : onClose}
            label={t("myProfile.profileSettings")}
            icon={ProfileSettingsIcon}
          />
          <HeaderMenuItem onClick={onClose} label={t("myProfile.securitySettings")} icon={SecurityIcon} />
          {isInstitutionAdmin && areSettingsAvailable && (
            <HeaderMenuItem
              onClick={() => navigateAndClose("/settings/institution")}
              label={t("myProfile.companySettings")}
              icon={InstitutionSettingsMenuIcon}
            />
          )}
          {isInstitutionAdmin && areSettingsAvailable && (
            <HeaderMenuItem
              onClick={() => navigateAndClose("/settings/userlist")}
              label={t("myProfile.users")}
              icon={PersonIcon}
            />
          )}
          {isSuperAdmin && (
            <>
              <br />
              <HeaderMenuItem onClick={onAdminAreaClick} label={t("myProfile.adminArea")} icon={AdminSettingsIcon} />
            </>
          )}
        </Box>
      )}
    </Menu>
  );
};
